import { TAG_KEYS } from '@components/imageHandling/common';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { formatLinkLabel } from '@v2/components/imageHandling/helpers';
import { AttachItemProps } from '@v2/components/imageHandling/components/Header/components/AttachModal/AttachItem';

interface UseManageAttachItemParams extends Pick<AttachItemProps, 'link' | 'onPress'> {}

export const useManageAttachItem = ({ link, onPress }: UseManageAttachItemParams) => {
  const { categoryName, referenceName } = formatLinkLabel(link);
  const isFavorite = !!link?.isFavorite;
  const categoryKey = TAG_KEYS[link?.scopeId as keyof typeof TAG_KEYS];
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const handlePress = () => {
    if (!link || !link.scopeId) return;
    onPress?.();

    // Navigation based on entity type
    switch (categoryKey) {
      case 'Part':
        navigateToMainDetailsScreen({
          mainScreen: PartDetailsScreenName,
          subScreen: PartDetailsScreen.PART,
          params: {
            perspective: Perspective.PART,
            partId: link.scopeReferenceId,
            partName: link.scopeReferenceName,
          },
        });
        break;

      case 'Clamping': // NC File
        navigateToMainDetailsScreen({
          mainScreen: NCFileDetailsScreenName,
          subScreen: NCFileDetailsScreen.NC_FILE,
          params: {
            perspective: Perspective.NC_FILE,
            ncFileId: link.scopeReferenceId,
            ncFileName: link.scopeReferenceName,
          },
        });
        break;

      case 'Operation':
        navigateToMainDetailsScreen({
          mainScreen: JobDetailsScreenName,
          subScreen: JobDetailsScreen.JOBS,
          params: {
            perspective: Perspective.JOBS,
            productionOperationId: link.scopeReferenceId,
          },
        });
        break;

      case 'ProductionOrder':
        navigateToMainDetailsScreen({
          mainScreen: ProductionOrderDetailsScreenName,
          subScreen: ProductionOrderDetailsScreen.ORDER,
          params: {
            perspective: Perspective.ORDER,
            productionOrderId: link.scopeReferenceId,
          },
        });
        break;

      case 'ProductionBatch':
        navigateToMainDetailsScreen({
          mainScreen: BatchDetailsScreenName,
          subScreen: BatchDetailsScreen.BATCH,
          params: {
            perspective: Perspective.BATCH,
            batchNumber: link.scopeReferenceName,
            batchId: link.scopeReferenceId,
            ncFileStatus: link.ncFileStatus,
          },
        });
        break;
    }
  };

  return { handlePress, categoryName, referenceName, categoryKey, isFavorite };
};
